import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A page's tab, kept in `?tab=`.
 *
 * One rule for every tabbed page: the page has one path, and the tab is a
 * query parameter. So a tab can be linked to, survives a reload, and comes
 * back with the browser's back button — and the sidebar, Ask AI and a
 * notification can all send a vendor straight to it with the same URL.
 *
 * An unknown or missing tab reads as the first one. Switching replaces the
 * history entry rather than pushing, so clicking through four tabs does not
 * take four presses of Back to leave the page. Other query parameters are
 * left alone.
 */
export function useTabParam<T extends string>(tabs: readonly T[], fallback: T = tabs[0]): [T, (next: T) => void] {
    const [params, setParams] = useSearchParams();
    const raw = params.get('tab');
    const tab = (tabs as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback;

    const setTab = useCallback(
        (next: T) => {
            const updated = new URLSearchParams(params);
            updated.set('tab', next);
            setParams(updated, { replace: true });
        },
        [params, setParams],
    );

    return [tab, setTab];
}
